import React, { useEffect, useId } from 'react';
import ThemeIcon from './ThemeIcon';
import { Link } from 'react-router-dom';
import usePageReady from '../hooks/usePageReady';
import './NotFound.css';
import { useSkin } from './SkinContext';
import SkinArtwork from './SkinArtwork';

const pageStates = {
  'not-found': { code: '404', eyebrow: 'A LITTLE DETOUR', title: '这一页，暂时找不到了。', description: '你要找的页面可能已移动，或暂时无法访问。', detail: '换一条路，精彩的故事还在继续。' },
  network: { code: 'OFFLINE', eyebrow: 'LET’S RECONNECT', title: '连接暂时中断了。', description: '暂时无法连接到博客，请检查网络后再试一次。', detail: '这段故事，我们待会儿继续。' },
  timeout: { code: 'TIMEOUT', eyebrow: 'TAKE A LITTLE MOMENT', title: '这一页，来得有点慢。', description: '等待页面的时间有些长，请稍后再试一次。', detail: '不妨歇一会儿，再接着往下读。' },
  server: { code: '…', eyebrow: '稍后再试', title: '博客正在短暂休息。', description: '博客暂时无法回应这次请求，请稍后重试。', detail: '等一小会儿，让故事继续。' },
  unauthorized: { code: '401', eyebrow: 'WELCOME BACK', title: '需要重新登录。', description: '登录状态已失效，请重新登录后继续。', detail: '你的创作，值得好好保存。' },
  forbidden: { code: '403', eyebrow: 'A LITTLE DETOUR', title: '暂时无法访问。', description: '当前账号没有访问这部分内容的权限。', detail: '也可以回到首页，看看其他故事。' },
  'rate-limit': { code: '429', eyebrow: 'TAKE A LITTLE MOMENT', title: '请稍等一下再试。', description: '刚刚的请求有些频繁，请稍等片刻再继续。', detail: '慢一点，故事也不会走远。' }
};

export default function NotFound({ kind = 'not-found', onRetry, compact = false, contextLabel = '内容' }) {
  usePageReady(!compact);
  const titleId = useId();
  const skin = useSkin();
  const resolvedKind = Object.prototype.hasOwnProperty.call(pageStates, kind) ? kind : 'server';
  const state = pageStates[resolvedKind];
  const compactDescriptions = {
    'not-found': `暂时无法找到${contextLabel}列表，请稍后再试。`,
    network: `暂时无法连接并加载${contextLabel}，请检查网络后再试。`,
    timeout: `${contextLabel}加载时间有些长，请稍后重试。`,
    server: `${contextLabel}暂时无法加载，请稍后再试。`,
    unauthorized: `请重新登录后查看${contextLabel}。`,
    forbidden: `当前账号暂时无法访问这些${contextLabel}。`,
    'rate-limit': `${contextLabel}请求有些频繁，请稍等片刻再试。`
  };
  const title = compact
    ? resolvedKind === 'unauthorized' || resolvedKind === 'forbidden' || resolvedKind === 'rate-limit'
      ? state.title : `${contextLabel}暂时没有加载出来。`
    : state.title;
  const Heading = compact ? 'h3' : 'h2';

  useEffect(() => {
    if (compact) return undefined;
    const previousTitle = document.title;
    const previousRobots = document.querySelector('meta[name="robots"]');
    const previousContent = previousRobots?.getAttribute('content');
    const robots = previousRobots || document.createElement('meta');
    document.title = `${state.title.replace(/。$/, '')} · 双霸服的霸服窝`;
    robots.setAttribute('name', 'robots');
    robots.setAttribute('content', 'noindex,follow');
    if (!previousRobots) document.head.appendChild(robots);
    return () => {
      document.title = previousTitle;
      if (!previousRobots) robots.remove();
      else if (previousContent === null) robots.removeAttribute('content');
      else robots.setAttribute('content', previousContent);
    };
  }, [compact, state.title]);

  return (
    <section className={`not-found-page${compact ? ' not-found-compact' : ''}`} data-kind={resolvedKind} aria-labelledby={titleId}>
      <div className="not-found-copy">
        <p className="not-found-eyebrow"><span aria-hidden="true" /> {state.eyebrow}</p>
        <div className={`not-found-code${state.code.length > 3 ? ' not-found-code-word' : ''}`} aria-hidden="true">{state.code}{resolvedKind === 'not-found' && <span>.</span>}</div>
        <Heading className="not-found-title" id={titleId}>{title}</Heading>
        <p className="not-found-description">{compact ? compactDescriptions[resolvedKind] : <>{state.description}<br />{state.detail}</>}</p>
        <div className="not-found-actions">
          {onRetry && (resolvedKind !== 'not-found' || compact) && <button type="button" className="not-found-link" onClick={onRetry}>重新尝试 <span aria-hidden="true"><ThemeIcon name="refresh" variant="action" /></span></button>}
          {resolvedKind === 'unauthorized' && <Link className="not-found-link" to="/admin/login">重新登录 <span aria-hidden="true"><ThemeIcon name="key" variant="action" /></span></Link>}
          <Link className="not-found-link icon-action" to="/" aria-label="返回首页" title="返回首页" data-tooltip="返回首页"><ThemeIcon name="home" variant="action" /></Link>
          {!compact && <Link className="not-found-link icon-action" to="/blog" aria-label="查看文章列表" title="查看文章列表" data-tooltip="查看文章列表"><ThemeIcon name="collection" variant="action" /></Link>}
        </div>
        {!compact && <p className="not-found-help">想留下一点想法？<Link to="/guestbook">去留言板坐坐</Link></p>}
      </div>
      <div className="not-found-illustration" aria-hidden="true">
        {['orbital', 'terminal', 'atelier'].includes(skin)
          ? <SkinArtwork skin={skin} className="not-found-artwork" />
          : <img className="not-found-artwork" src={`${process.env.PUBLIC_URL || ''}/illustrations/not-found-journal.svg`} alt="" width="560" height="480" />}
        {!compact && <span className="not-found-caption"><span /> 每一次转弯，都可能遇见新的一页。</span>}
      </div>
    </section>
  );
}
